import React, {useState, useEffect, useMemo} from 'react';
import {Text, Button, OtpInput, Modal, Icon} from '@/ui-kits';
import {maskEmail, maskPhone} from '@/utils/common_functions';
import {useDispatch, useSelector} from 'react-redux';
import {emailOtpAttemptsSelector, emailOtpTimerSelector, phoneOtpAttemptsSelector, phoneOtpTimerSelector} from '@/services/redux/selectors';
import {useFormik} from 'formik';
import {OTPValidation} from '@/utils/schema';
import {profileOtpResendRequest} from '@/services/redux/slices';
import {OTPResendButton} from '../auth-screen';
import {resetOtpTimer} from '@/constants';

interface OTPVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (otp: string) => void;
  phoneNumber?: string;
  email?: string;
  type: 'phone' | 'email';
}

export const OTPVerificationModal: React.FC<OTPVerificationModalProps> = ({isOpen, onClose, onVerify, phoneNumber, email, type}) => {
  /**
   * hooks
   */
  const emailOtpAttempts = useSelector(emailOtpAttemptsSelector);
  const phoneOtpAttempts = useSelector(phoneOtpAttemptsSelector);

  const emailOtpTimer = useSelector(emailOtpTimerSelector);
  const phoneOtpTimer = useSelector(phoneOtpTimerSelector);

  const attemptsLeft = useMemo(() => (type === 'email' ? emailOtpAttempts : phoneOtpAttempts), [type, emailOtpAttempts, phoneOtpAttempts]);
  const otpLastUpdate = useMemo(() => (type === 'email' ? emailOtpTimer : phoneOtpTimer), [type, emailOtpTimer, phoneOtpTimer]);

  const dispatch = useDispatch();
  const {handleSubmit, errors, touched, handleChange, handleBlur, values, isValid, setFieldValue, setFieldTouched} = useFormik({
    initialValues: {otp: ''},
    validationSchema: OTPValidation,
    onSubmit,
  });

  /**
   * states
   */
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const canResendOTP = useMemo<boolean>(() => Boolean(Number(remainingTime) <= 0), [remainingTime]);

  const resetOtp = () => {
    setFieldValue('otp', '');
    setFieldTouched('otp', false);
  };

  /**
   * functions
   */
  function onSubmit(values: {otp: string}) {
    onVerify(values.otp);
    resetOtp();
  }

  const maskedContact = type === 'phone' ? maskPhone(phoneNumber!) : maskEmail(email!, false);

  const handleResend = () => {
    dispatch(profileOtpResendRequest({type}));
    resetOtp();
  };

  /**
   * side effects
   */
  useEffect(() => {
    const updateTimer = () => {
      if (otpLastUpdate) {
        const timeElapsed = Math.floor((Date.now() - new Date(otpLastUpdate).getTime()) / 1000);
        const newRemainingTime = Math.max(resetOtpTimer - timeElapsed, 0);
        setRemainingTime(newRemainingTime);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [otpLastUpdate]);

  return (
    <Modal open={isOpen} className="sm:max-w-[420px] flex items-center justify-center flex-col gap-2 rounded-2xl shadow-lg">
      {/* Close Button */}
      <button onClick={onClose} className="absolute top-3 right-3 size-8">
        <Icon type="close" />
      </button>

      {/* Header */}
      <Text className="text-center text-ui_blue font-FigtreeRegular mb-4 whitespace-nowrap">Enter the OTP sent to {maskedContact}</Text>

      {/* OTP Input */}
      <div className="flex justify-center mb-4 space-x-3">
        <OtpInput
          value={values.otp}
          onChange={handleChange('otp')}
          onBlur={handleBlur('otp')}
          error={errors.otp && ' '}
          touched={touched.otp}
          length={6}
          inputClassName="size-10 border border-gray-300 rounded-md text-center text-lg"
        />
      </div>

      {/* Error / Attempts */}
      {touched.otp && errors.otp && <div className="text-center text-red-500 text-sm mb-2">{errors.otp}</div>}
      <div className="text-center text-ui_blue font-FigtreeRegular text-sm mb-2">{attemptsLeft} Attempts Left</div>

      {/* Resend */}
      <OTPResendButton canResendOTP={canResendOTP} remainingTime={remainingTime} resendOTP={handleResend} />

      {/* Submit Button */}
      <Button
        onClick={() => handleSubmit()}
        title="Submit OTP"
        variant="primary"
        disabled={values.otp.length !== 6 || attemptsLeft <= 0 || !isValid}
        className="w-50%"
      />
    </Modal>
  );
};
